// Disabling ESLint rules only because this code is checked by TypeScript directly
/* eslint-disable jsdoc/check-types */
/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable jsdoc/valid-types */

import { useSyncExternalStore } from 'react';

const returnUndefined = () => undefined;

/**
 * @template State
 * @typedef {{
 *   pushState: (state: State, url?: string) => void;
 *   replaceState: (state: State, url?: string) => void;
 * }} HistoryApi
 */

/**
 * Creates a useHistoryState hook that lets you use pushState/replaceState and have your components
 * re-render with the updated data
 *
 * @template State
 * @param {string} key A unique key for storing state on window.history.state.  This key should be
 * unique across your application.  Updating state will only change this key on window.history.state,
 * and changes to the value under this key are the only changes that will cause your component to
 * re-render.
 * @param {State} defaultState The default state to use if no state is found in history
 */
export function setupUseHistoryState(key, defaultState) {
  /** @typedef {() => void} Listener */

  /** @type {Set<Listener>} */
  const listeners = new Set();

  /**
   * @returns {HistoryApi<State>}
   */
  function createHistoryApi() {
    return {
      pushState(state, url) {
        window.history.pushState({ ...window.history.state, [key]: state }, '', url);
        listeners.forEach((listener) => listener());
      },
      replaceState(state, url) {
        window.history.replaceState({ ...window.history.state, [key]: state }, '', url);
        listeners.forEach((listener) => listener());
      },
    };
  }

  /**
   * @returns {State}
   */
  function getSnapshot() {
    return window.history.state?.[key] ?? defaultState;
  }

  /**
   * @param {Listener} callback
   */
  function subscribe(callback) {
    listeners.add(callback);

    window.addEventListener('popstate', () => {
      callback();
    });

    return () => {
      listeners.delete(callback);
      window.removeEventListener('popstate', callback);
    };
  }

  /**
   * Hook that lets you use pushState/replaceState and have your components
   * re-render with the updated data
   *
   * @returns {{
   *   history: HistoryApi<State>,
   *   state: State,
   * }}
   */
  return function useHistoryState() {
    const store = useSyncExternalStore(subscribe, getSnapshot, returnUndefined);

    return {
      history: createHistoryApi(),
      state: store ?? defaultState,
    };
  };
}
